import { CurrencyDollarIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { OtsActionButton } from "./OtsButton";
import {
  COSTING_PROGRESS_ATTRIBUTE,
  COSTING_STARTED_AT_ATTRIBUTE,
  type CostingProgress,
} from "./OtsCostingProgressHost";
import { OtsGoBotPopOut } from "./OtsGoBotPopOut";

export type OtsCostingButtonStatus = "idle" | "running" | "done" | "failed";

export type OtsCostingButtonProps = {
  label: string;
  status?: OtsCostingButtonStatus;
  /**
   * The server's stages while costing runs. They ride on the button as WaterGo's money cell carries them, so the
   * shared `OtsCostingProgressHost` shows its progress tooltip once costing has taken a while.
   */
  progress?: CostingProgress | null;
  /** When costing started, for the tooltip's delay. */
  startedAt?: number;
  /** Why the last costing failed, beside the button. */
  error?: string;
  onClick: () => void;
  className?: string;
};

// WaterGo's coin while costing: a ring sweeps round a coin that flips. Reduced motion keeps only a slow ring.
const costingCss = [
  "@keyframes ots-costing-spin{to{transform:rotate(360deg)}}",
  "@keyframes ots-costing-flip{0%{transform:perspective(40px) rotateY(0)}50%{transform:perspective(40px) rotateY(180deg)}100%{transform:perspective(40px) rotateY(360deg)}}",
  ".ots-costing-ring{animation:ots-costing-spin .9s linear infinite;background:conic-gradient(from 0deg,transparent 0deg,currentColor 300deg,transparent 360deg);-webkit-mask:radial-gradient(farthest-side,transparent calc(100% - 2px),#000 calc(100% - 2px));mask:radial-gradient(farthest-side,transparent calc(100% - 2px),#000 calc(100% - 2px))}",
  ".ots-costing-coin{animation:ots-costing-flip 1.2s cubic-bezier(.45,0,.55,1) infinite}",
  "@media (prefers-reduced-motion:reduce){.ots-costing-ring{animation-duration:2.4s}.ots-costing-coin{animation:none}}",
].join("");

// What the host reads before the first stage arrives, as WaterGo seeds it.
const STARTING: CostingProgress = { state: "RUNNING", currentLabel: "Starting costing", stages: [] };

/**
 * Costs something in place: the button itself shows costing running (WaterGo's ring and flipping coin), carries
 * its progress for the shared tooltip, and pops GoBot out of its coin when it is done.
 */
export const OtsCostingButton = ({ label, status = "idle", progress, startedAt, error, onClick, className }: OtsCostingButtonProps) => {
  const running = status === "running";
  return (
    <span className={["relative inline-flex items-center gap-1.5", className].filter(Boolean).join(" ")}>
      <style>{costingCss}</style>
      {status === "failed" && (
        <span role="status" className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-[var(--ots-error)]">
          <ExclamationTriangleIcon aria-hidden="true" className="size-3.5"/>
          {error ? `Costing failed: ${error}` : "Costing failed"}
        </span>
      )}
      <OtsActionButton
        variant="text"
        size="compact"
        aria-busy={running || undefined}
        aria-label={running ? `${label}: costing` : label}
        {...(running ? {
          [COSTING_PROGRESS_ATTRIBUTE]: JSON.stringify(progress ?? STARTING),
          [COSTING_STARTED_AT_ATTRIBUTE]: String(startedAt ?? Date.now()),
        } : {})}
        // Repeat clicks while it runs do nothing, as in WaterGo; the button stays focusable for the tooltip.
        onClick={running ? undefined : onClick}
        icon={running
          ? <span data-costing="running" className="relative flex size-full items-center justify-center">
            <span className="ots-costing-ring absolute -inset-[3px] rounded-full"/>
            <CurrencyDollarIcon className="ots-costing-coin size-full"/>
          </span>
          : <CurrencyDollarIcon/>}>
        {running ? "Costing…" : label}
      </OtsActionButton>
      {/* GoBot pops out of the button's coin. */}
      {status === "done" && <span data-costing="done" className="absolute left-3.5 top-1/2 z-20"><OtsGoBotPopOut/></span>}
      {status === "done" && <span role="status" className="sr-only">Costed</span>}
    </span>
  );
};
